import React from 'react';

import EventIcon from '@mui/icons-material/Event';
import PhoneIcon from '@mui/icons-material/Phone';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@/components/NewUI/ComponentsLibrary';

import { formatLongDay, formatSlot, formatTime, zoneAbbreviation } from './scheduling';
import { styles } from './styles';
import { aiButtonSx } from '../aiStyles';

import type { ReactNode } from 'react';

export interface ICallSummary {
  slotIso: string | null;
  timeZone: string;
  // Already safe to show: the masked number on file, or a valid override.
  number: string;
  numberIsOverride: boolean;
  // Why Confirm is disabled, if it is.
  blocker: 'no_slot' | 'invalid_number' | null;
  submitting: boolean;
  // A booking request that failed for a reason the candidate can retry.
  submitError?: string | null;
  onConfirm: () => void;
}

const SummaryRow = ({
  icon,
  term,
  value,
  placeholder,
  note
}: {
  icon: ReactNode;
  term: string;
  value: string | null;
  placeholder?: string;
  note?: string | null;
}) => (
  <Stack component="li" sx={styles.summaryRow}>
    {icon}
    <Box>
      <Typography sx={value ? styles.summaryValue : styles.summaryPlaceholder}>
        <Typography component="span" sx={styles.visuallyHidden}>
          {`${term}: `}
        </Typography>
        {value ?? placeholder}
      </Typography>
      {note ? <Typography sx={styles.summaryNote}>{note}</Typography> : null}
    </Box>
  </Stack>
);

const blockerText = (blocker: ICallSummary['blocker']) => {
  if (blocker === 'no_slot') return 'Choose a date and time to continue.';
  if (blocker === 'invalid_number') return 'Enter a valid phone number to continue.';
  return null;
};

// "Your call": the pick so far and Confirm, beside the picker on wider screens.
export const CallSummaryPanel = ({
  slotIso,
  timeZone,
  number,
  numberIsOverride,
  blocker,
  submitting,
  submitError,
  onConfirm
}: ICallSummary) => {
  const hint = blockerText(blocker);
  return (
    <Stack aria-labelledby="booking-call-heading" component="section" sx={styles.card}>
      <Typography component="h2" id="booking-call-heading" sx={styles.cardHeading}>
        Your call
      </Typography>
      <Stack aria-live="polite" component="ul" sx={styles.summaryList}>
        <SummaryRow
          icon={<EventIcon aria-hidden />}
          placeholder="Pick a date"
          term="Date"
          value={slotIso ? formatLongDay(slotIso, timeZone) : null}
        />
        <SummaryRow
          icon={<ScheduleIcon aria-hidden />}
          placeholder="Pick a time"
          term="Time"
          value={
            slotIso
              ? `${formatTime(slotIso, timeZone)} ${zoneAbbreviation(slotIso, timeZone)}`
              : null
          }
        />
        <SummaryRow
          icon={<PhoneIcon aria-hidden />}
          note={numberIsOverride ? 'For this interview only' : null}
          term="Rosie will call"
          value={number}
        />
      </Stack>
      <Box sx={styles.summaryDivider} />
      <Button
        fullWidth
        buttonColor="purple"
        buttonVariant="primary"
        disabled={blocker !== null}
        id="booking-confirm"
        loading={submitting}
        onClick={onConfirm}
        sx={aiButtonSx}
      >
        Confirm call time
      </Button>
      {hint ? <Typography sx={styles.confirmHint}>{hint}</Typography> : null}
      {submitError ? (
        <Typography role="alert" sx={styles.mobileBarError}>
          {submitError}
        </Typography>
      ) : null}
    </Stack>
  );
};

// Phones: the same summary in a bar stuck to the bottom of the screen.
export const MobileConfirmBar = ({
  slotIso,
  timeZone,
  number,
  blocker,
  submitting,
  submitError,
  onConfirm
}: Omit<ICallSummary, 'numberIsOverride'>) => (
  <Box aria-label="Your call" component="section" sx={styles.mobileBar}>
    <Stack aria-live="polite" sx={styles.mobileBarText}>
      <Typography sx={slotIso ? styles.mobileBarMain : styles.mobileBarPlaceholder}>
        {slotIso ? formatSlot(slotIso, timeZone) : 'Choose a time'}
      </Typography>
      {submitError || blocker === 'invalid_number' ? (
        <Typography sx={styles.mobileBarError}>
          {submitError ?? 'Enter a valid phone number'}
        </Typography>
      ) : (
        <Typography sx={styles.mobileBarSub}>{`Rosie will call ${number}`}</Typography>
      )}
    </Stack>
    <Button
      buttonColor="purple"
      buttonVariant="primary"
      disabled={blocker !== null}
      id="booking-confirm-mobile"
      loading={submitting}
      minWidth="140px"
      onClick={onConfirm}
      sx={aiButtonSx}
    >
      Confirm call time
    </Button>
  </Box>
);
